function waitForFlyBundleElement(selector, callback) {
  const element = document.querySelector(selector);
  if (element) {
    callback(element);
    return;
  }

  const observer = new MutationObserver(() => {
    const element = document.querySelector(selector);
    if (element) {
      observer.disconnect();
      callback(element);
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function getFBTContainerByOfferId(offerId) {
  return document.querySelector(
    `.gfbFrequentBoughtWidgetContainer[data-offer-id="${offerId}"]`,
  );
}

function updateFBTButtonPrice(container) {
  if (!container) return;

  const priceEl = container.querySelector(
    ".gfbFrequentBoughtWidgetPriceContainerPriceDiscounted",
  );
  const button = container.querySelector(
    ".gfbFrequentBoughtAddToCartButton",
  );

  if (!priceEl || !button || button.classList.contains("gfbDisableAddToCart"))
    return;

  let priceSpan = button.querySelector(".fbt-price");
  if (!priceSpan) {
    priceSpan = document.createElement("span");
    priceSpan.className = "fbt-price";
    button.appendChild(priceSpan);
  }

  priceSpan.textContent = ` ${priceEl.textContent.trim()}`;
}

function initFBTShowMoreWithDividers({
  containerSelector = ".gfbFrequentBoughtWidgetContentContainer",
  productSelector = ".gfbFrequentBoughtWidgetProductContainer",
  dividerSelector = ".gfbHorizontalPlusImageBarContainer",
  itemsLimit = 4,
} = {}) {
  document.querySelectorAll(containerSelector).forEach((container) => {
    if (container.dataset.fbtInitialized) return;
    container.dataset.fbtInitialized = "true";

    const products = [...container.querySelectorAll(productSelector)];
    if (products.length <= itemsLimit) return;

    const setCollapsed = (collapsed) => {
      products.forEach((product, index) => {
        const divider = product.nextElementSibling?.matches(dividerSelector)
          ? product.nextElementSibling
          : null;
        const shouldShow = !collapsed || index < itemsLimit;

        product.style.display = shouldShow ? "" : "none";
        if (divider) divider.style.display = shouldShow ? "" : "none";
        product.classList.toggle("fbt-limit-item", index === itemsLimit - 1);
      });

      container.classList.toggle("is-collapsed", collapsed);
      container.classList.toggle("is-expanded", !collapsed);
    };

    setCollapsed(true);

    const toggleButton = document.createElement("button");
    toggleButton.type = "button";
    toggleButton.className = "button button--outline fbt-toggle";

    const parent = container.closest(".product-bundle");
    const showMoreText = parent?.dataset.showMoreText || "Show more";
    const showLessText = parent?.dataset.showLessText || "Show less";
    toggleButton.textContent = showMoreText;

    toggleButton.addEventListener("click", () => {
      const isCollapsed = container.classList.contains("is-collapsed");
      setCollapsed(!isCollapsed);
      toggleButton.textContent = isCollapsed ? showLessText : showMoreText;
    });

    container.appendChild(toggleButton);
  });
}

/**
 * @param {string} query
 * @param {Object} [variables]
 * @returns {Promise<Object|null>}
 */
async function storefrontFetch(query, variables = {}) {
  const token = window.STOREFRONT_API_TOKEN;
  if (!token) return null;
  const response = await fetch("/api/2024-10/graphql.json", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": token,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`Storefront API ${response.status}`);
  const json = await response.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json.data;
}

/** @type {Map<string, Array<Object>>} */
const FBT_MAXIMUM_VARIANT_CACHE = new Map();
const FBT_MAXIMUM_VARIANT_QUERY = `
  query FBTProductMaximumData($handle: String!) {
    productByHandle(handle: $handle) {
      variants(first: 100) {
        nodes {
          id
          selectedOptions { value }
          metafield(namespace: "custom", key: "maximum_order_quantity") { value }
        }
      }
    }
  }
`;

async function fetchFBTMaximumVariants(handle) {
  if (FBT_MAXIMUM_VARIANT_CACHE.has(handle)) {
    return FBT_MAXIMUM_VARIANT_CACHE.get(handle);
  }

  const data = await storefrontFetch(FBT_MAXIMUM_VARIANT_QUERY, { handle });
  const variants = data?.productByHandle?.variants?.nodes || [];
  FBT_MAXIMUM_VARIANT_CACHE.set(handle, variants);
  return variants;
}

function getFBTSelectedVariant(productContainer, variants) {
  const selectedValues = new Map();
  const selectedPills = [
    ...productContainer.querySelectorAll(
      ".gfbFrequentBoughtLabelPillsUISelected",
    ),
  ];
  selectedPills.forEach((pill) => {
    const optionIndex = parseInt(pill.dataset.optionIndex, 10);
    if (!Number.isNaN(optionIndex)) {
      selectedValues.set(optionIndex, pill.dataset.optionValue);
    }
  });

  const variantIdElements = [productContainer, ...selectedPills];
  for (const element of variantIdElements) {
    const datasetVariantId =
      element.dataset.variantId ||
      element.dataset.productVariantId ||
      element.dataset.selectedVariantId;
    if (!datasetVariantId) continue;
    const id = String(datasetVariantId).split("/").pop();
    const datasetMatch = variants.find(
      (variant) => variant.id.split("/").pop() === id,
    );
    if (datasetMatch) return datasetMatch;
  }

  if (!selectedValues.size) return variants.length === 1 ? variants[0] : null;
  const indexedMatch = variants.find((variant) =>
    [...selectedValues].every(
      ([index, value]) =>
        variant.selectedOptions[index]?.value === value ||
        variant.selectedOptions[index - 1]?.value === value,
    ),
  );
  if (indexedMatch) return indexedMatch;

  const selectedOptionValues = [...selectedValues.values()];
  const valueMatches = variants.filter((variant) => {
    const variantValues = variant.selectedOptions.map((option) => option.value);
    return selectedOptionValues.every((value) => variantValues.includes(value));
  });
  return valueMatches.length === 1 ? valueMatches[0] : null;
}

function getFBTProductHandle(productContainer) {
  if (productContainer.dataset.productHandle) {
    return productContainer.dataset.productHandle;
  }

  const productLink = productContainer.querySelector('a[href*="/products/"]');
  if (!productLink) return null;
  const match = productLink
    .getAttribute("href")
    ?.match(/\/products\/([^/?#]+)/);
  return match?.[1] || null;
}

function getFBTCartVariantTotals(cart) {
  const totals = new Map();
  const add = (id, quantity) => {
    const variantId = String(id);
    totals.set(variantId, (totals.get(variantId) || 0) + quantity);
  };

  cart.items.forEach((item) => {
    add(item.variant_id, item.quantity);
    const details = item.properties?._Details;
    if (typeof details !== "string") return;

    const pattern = /(\d+)\s*:\s*(\d+)/g;
    let match;
    while ((match = pattern.exec(details)) !== null) {
      add(match[1], parseInt(match[2], 10) || 0);
    }
  });

  return totals;
}

let fbtCartMaterialised = false;
async function ensureFBTCartMaterialised() {
  if (fbtCartMaterialised) return;
  fbtCartMaterialised = true;

  try {
    const root = window.Shopify.routes.root;
    const cart = await (await fetch(`${root}cart.js`)).json();
    if (cart.item_count > 0) return;

    await fetch(`${root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: {} }),
    });
  } catch (error) {
    console.error("[FBT cart materialise]", error);
  }
}

function isCurrentProductBlockedBundle() {
  const theme = window.ttTheme || {};
  if (!theme.bundleOosEnabled) return false;

  const prefixes = String(theme.bundleOosPrefixes || "")
    .split(",")
    .map((prefix) => prefix.trim().toUpperCase())
    .filter(Boolean);

  if (!prefixes.length) return false;

  const variants = window.ShopifyAnalytics?.meta?.product?.variants || [];
  return variants.some((variant) => {
    const sku = String(variant.sku || "").toUpperCase();
    return prefixes.some((prefix) => sku.startsWith(prefix));
  });
}

/**
 * The Fly Bundle widget injects its own add-to-cart button
 * (.gfbFrequentBoughtAddToCartButton) a few seconds after load and re-renders
 * it on interaction. Hide and block the app button in content-only markets.
 * For MBP-700 blocked-bundle PDPs, disable, relabel and block the same button.
 */
let fbtAddToCartClickBlocked = false;
function enforceBundleOosOnFBT(container) {
  const isContentOnly = window.ttTheme?.contentOnly === true;
  const isBlockedBundle = isCurrentProductBlockedBundle();
  if (!isContentOnly && !isBlockedBundle) return;

  const label =
    window.variantStrings?.bundleUnavailable || "Currently unavailable";

  const enforceButtons = () => {
    document
      .querySelectorAll(".gfbFrequentBoughtAddToCartButton")
      .forEach((button) => {
        if (isContentOnly) {
          // Fly Bundle can override [hidden], so retain the inline fallback.
          if (
            button.dataset.contentOnly === "1" &&
            button.hidden &&
            button.disabled &&
            button.style.display === "none"
          ) {
            return;
          }
          button.dataset.contentOnly = "1";
          button.hidden = true;
          button.disabled = true;
          button.setAttribute("disabled", "disabled");
          button.style.display = "none";
          button.style.pointerEvents = "none";
          return;
        }

        // Skip if already in our settled disabled state (avoids observer loop)
        if (button.dataset.bundleOos === "1" && button.disabled) return;

        button.dataset.bundleOos = "1";
        button.disabled = true;
        button.setAttribute("disabled", "disabled");
        button.classList.add("gfbDisableAddToCart");
        button.style.pointerEvents = "none";

        const priceSpan = button.querySelector(".fbt-price");
        if (priceSpan) priceSpan.remove();
        if (button.textContent.trim() !== label) button.textContent = label;
      });
  };

  enforceButtons();

  const root =
    container?.closest(".product-bundle") || container || document.body;
  new MutationObserver(enforceButtons).observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "disabled", "hidden", "style"],
  });

  if (!fbtAddToCartClickBlocked) {
    fbtAddToCartClickBlocked = true;
    document.addEventListener(
      "click",
      (event) => {
        if (
          window.ttTheme?.contentOnly !== true &&
          !isCurrentProductBlockedBundle()
        ) {
          return;
        }
        if (event.target.closest?.(".gfbFrequentBoughtAddToCartButton")) {
          event.stopPropagation();
          event.preventDefault();
        }
      },
      true,
    );
  }
}

let fbtMaximumOrderBlocked = false;
function initFBTMaximumOrderMessaging(container) {
  const bundleMount =
    container.closest(".product-bundle") ||
    document.querySelector(
      ".product-bundle[data-fly-bundle-parent-variant-id]",
    );
  const root = bundleMount?.parentElement || container.parentElement;
  const message = bundleMount?.nextElementSibling?.matches(
    ".product-bundle__maximum-order-message",
  )
    ? bundleMount.nextElementSibling
    : root?.querySelector(".product-bundle__maximum-order-message");
  if (!root || !bundleMount || !message) return;
  const buttonRoot = container;

  let idleButtonState = null;
  let cartRecipeMaximumBlocked = false;
  let selectedComponentsMaximumBlocked = false;
  let requestMaximumBlocked = false;
  const selectedComponentCache = new Map();

  const captureIdleButtonState = (button) => {
    idleButtonState = {
      html: button.innerHTML,
      className: button.className,
      disabled: button.disabled,
      ariaDisabled: button.getAttribute("aria-disabled"),
      pointerEvents: button.style.pointerEvents,
    };
  };

  const restoreIdleButtonState = (button) => {
    if (idleButtonState?.html && button.innerHTML !== idleButtonState.html) {
      button.innerHTML = idleButtonState.html;
    }

    ["loading", "is-loading", "gfbLoading", "gfbAddToCartLoading"].forEach(
      (className) => button.classList.remove(className),
    );
    button.removeAttribute("aria-busy");
    button.removeAttribute("data-loading");
  };

  const disableButtons = () => {
    if (!fbtMaximumOrderBlocked) return;
    buttonRoot
      .querySelectorAll(".gfbFrequentBoughtAddToCartButton")
      .forEach((button) => {
        restoreIdleButtonState(button);
        if (idleButtonState?.className) {
          const disabledClassName = `${idleButtonState.className} gfbDisableAddToCart`;
          if (button.className !== disabledClassName) {
            button.className = disabledClassName;
          }
        }
        if (button.dataset.bundleMaximumDisabled === "true") {
          if (!button.disabled) button.disabled = true;
          if (button.style.pointerEvents !== "none") {
            button.style.pointerEvents = "none";
          }
          return;
        }
        button.dataset.bundleMaximumDisabled = "true";
        const capturedIdleState = idleButtonState;
        button.dataset.bundleMaximumWasDisabled = String(
          capturedIdleState?.disabled ?? button.disabled,
        );
        button.dataset.bundleMaximumAriaDisabled =
          capturedIdleState?.ariaDisabled ??
          button.getAttribute("aria-disabled") ??
          "";
        button.dataset.bundleMaximumPointerEvents =
          capturedIdleState?.pointerEvents ?? button.style.pointerEvents;
        button.disabled = true;
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-disabled", "true");
        button.classList.add("gfbDisableAddToCart");
        button.style.pointerEvents = "none";
      });
  };

  const clearMaximumState = () => {
    fbtMaximumOrderBlocked = false;
    message.hidden = true;
    buttonRoot
      .querySelectorAll('[data-bundle-maximum-disabled="true"]')
      .forEach((button) => {
        const capturedIdleState = idleButtonState;
        if (capturedIdleState) {
          button.innerHTML = capturedIdleState.html;
          button.className = capturedIdleState.className;
          button.disabled = capturedIdleState.disabled;
          button.toggleAttribute("disabled", capturedIdleState.disabled);
        } else if (button.dataset.bundleMaximumWasDisabled !== "true") {
          button.disabled = false;
          button.removeAttribute("disabled");
          button.classList.remove("gfbDisableAddToCart");
        }
        const ariaDisabled = capturedIdleState
          ? capturedIdleState.ariaDisabled
          : button.dataset.bundleMaximumAriaDisabled;
        if (ariaDisabled) button.setAttribute("aria-disabled", ariaDisabled);
        else button.removeAttribute("aria-disabled");
        button.style.pointerEvents = capturedIdleState
          ? capturedIdleState.pointerEvents
          : button.dataset.bundleMaximumPointerEvents || "";
        delete button.dataset.bundleMaximumDisabled;
        delete button.dataset.bundleMaximumWasDisabled;
        delete button.dataset.bundleMaximumAriaDisabled;
        delete button.dataset.bundleMaximumPointerEvents;
      });
  };

  const showMaximumState = () => {
    fbtMaximumOrderBlocked = true;
    if (!message.textContent.trim()) {
      message.textContent =
        window.variantStrings?.bundleMaximumOrderQuantityReached || "";
    }
    message.hidden = false;
    disableButtons();
    requestAnimationFrame(disableButtons);
    setTimeout(disableButtons, 100);
    setTimeout(disableButtons, 500);
  };

  const reconcileMaximumState = () => {
    if (
      cartRecipeMaximumBlocked ||
      selectedComponentsMaximumBlocked ||
      requestMaximumBlocked
    ) {
      showMaximumState();
    } else {
      clearMaximumState();
    }
  };

  let maximumPreflightRun = 0;
  const evaluateSelectedBundleMaximum = async () => {
    const run = ++maximumPreflightRun;
    const productContainers = [
      ...container.querySelectorAll(".gfbFrequentBoughtWidgetProductContainer"),
    ];
    if (!productContainers.length) return;

    const currentHandles = new Set(
      productContainers.map(getFBTProductHandle).filter(Boolean),
    );
    selectedComponentCache.forEach((_selection, handle) => {
      if (!currentHandles.has(handle)) selectedComponentCache.delete(handle);
    });

    try {
      const [cartResponse, selections] = await Promise.all([
        fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: "application/json" },
        }),
        Promise.all(
          productContainers.map(async (productContainer) => {
            const handle = getFBTProductHandle(productContainer);
            if (!handle) return null;
            const variants = await fetchFBTMaximumVariants(handle);
            const variant = getFBTSelectedVariant(productContainer, variants);
            if (!variant) return null;
            return {
              handle,
              variantId: variant.id.split("/").pop(),
              maximum: parseInt(variant.metafield?.value, 10) || null,
              quantity:
                parseInt(
                  productContainer.dataset.quantity ||
                    productContainer.dataset.productQuantity,
                  10,
                ) || 1,
            };
          }),
        ),
      ]);
      if (run !== maximumPreflightRun || !cartResponse.ok) return;

      const cart = await cartResponse.json();
      const totals = getFBTCartVariantTotals(cart);
      const resolvedSelections = selections.filter(Boolean);
      resolvedSelections.forEach((selection) => {
        selectedComponentCache.set(selection.handle, selection);
      });

      const selectionsToEvaluate = [...currentHandles]
        .map((handle) => selectedComponentCache.get(handle))
        .filter(Boolean);
      if (!selectionsToEvaluate.length) return;
      const blocked = selectionsToEvaluate.some(
        (selection) =>
          selection.maximum &&
          (totals.get(selection.variantId) || 0) + selection.quantity >
            selection.maximum,
      );

      if (blocked) {
        selectedComponentsMaximumBlocked = true;
        reconcileMaximumState();
      } else {
        selectedComponentsMaximumBlocked = false;
        reconcileMaximumState();
      }
    } catch (error) {
      console.error("[Bundle maximum quantity]", error);
    }
  };

  document.addEventListener("bundle:maximum-order-error", () => {
    requestMaximumBlocked = true;
    reconcileMaximumState();
  });
  document.addEventListener("bundle:maximum-order-state", (event) => {
    const blockedIds = new Set(
      (event.detail?.blockedParentVariantIds || []).map(String),
    );
    const currentProductVariantIds = [
      bundleMount.dataset.flyBundleParentVariantId,
      ...(window.ttTheme?.flyBundleParentVariantIds || []),
    ].filter(Boolean);
    const currentProductBlocked = currentProductVariantIds.some((id) =>
      blockedIds.has(String(id)),
    );
    cartRecipeMaximumBlocked = currentProductBlocked;
    requestMaximumBlocked = false;
    reconcileMaximumState();
  });

  buttonRoot.addEventListener(
    "click",
    (event) => {
      const addButton = event.target.closest(
        ".gfbFrequentBoughtAddToCartButton",
      );
      if (addButton) {
        if (fbtMaximumOrderBlocked) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        captureIdleButtonState(addButton);
      }

      if (event.target.closest(".gfbFrequentBoughtLabelPillsUI")) {
        const productContainer = event.target.closest(
          ".gfbFrequentBoughtWidgetProductContainer",
        );
        const handle = productContainer
          ? getFBTProductHandle(productContainer)
          : null;
        if (handle) selectedComponentCache.delete(handle);
        idleButtonState = null;
        selectedComponentsMaximumBlocked = false;
        requestMaximumBlocked = false;
        reconcileMaximumState();
        setTimeout(evaluateSelectedBundleMaximum, 100);
      }
    },
    true,
  );

  const initialProductVariantIds = [
    bundleMount.dataset.flyBundleParentVariantId,
    ...(window.ttTheme?.flyBundleParentVariantIds || []),
  ].filter(Boolean);
  const initiallyBlocked = initialProductVariantIds.some((id) =>
    window.bundleMaximumBlockedVariantIds?.has(String(id)),
  );
  cartRecipeMaximumBlocked = initiallyBlocked;
  reconcileMaximumState();
  evaluateSelectedBundleMaximum();
  document.addEventListener("cart:updated", evaluateSelectedBundleMaximum);
  document.addEventListener("gfb:syncProductsPriceForFixedPriceBundle", () =>
    setTimeout(evaluateSelectedBundleMaximum, 50),
  );

  let maximumPreflightTimer;
  new MutationObserver(() => {
    disableButtons();
    clearTimeout(maximumPreflightTimer);
    maximumPreflightTimer = setTimeout(evaluateSelectedBundleMaximum, 150);
  }).observe(buttonRoot, {
    childList: true,
    subtree: true,
  });
}

waitForFlyBundleElement(".gfbFrequentBoughtWidgetContainer", (element) => {
  ensureFBTCartMaterialised();
  enforceBundleOosOnFBT(element);
  initFBTShowMoreWithDividers();
  updateFBTButtonPrice(element);
  initFBTMaximumOrderMessaging(element);
});

document.addEventListener(
  "gfb:syncProductsPriceForFixedPriceBundle",
  (event) => {
    const { offerId } = event.detail || {};
    if (!offerId) return;

    const container = getFBTContainerByOfferId(offerId);
    if (!container) return;

    requestAnimationFrame(() => {
      updateFBTButtonPrice(container);
    });
  },
);
